import Link from "next/link";
import { appendOutboundCampaign } from "@punkrecords/card-utils";

const SCRYFALL_HOME_URL = appendOutboundCampaign("https://scryfall.com", "footer_scryfall");
const SCRYFALL_DONATE_URL = appendOutboundCampaign("https://scryfall.com/donate", "footer_scryfall_donate");

type FooterProps = {
  idPrefix?: string;
};

export function Footer({ idPrefix = "footer" }: FooterProps) {
  const base = idPrefix;
  const buildVersion = process.env.NEXT_PUBLIC_BUILD_VERSION;

  return (
    <footer
      id={`${base}-root`}
      className="border-t border-slate-800 bg-slate-950/80"
    >
      <div
        id={`${base}-content`}
        className="mx-auto grid max-w-5xl gap-8 px-4 py-8 text-sm text-slate-300 md:grid-cols-[2fr_1fr_1fr_1fr]"
      >
        <div id={`${base}-brand`} className="space-y-3">
          <div id={`${base}-brand-name`} className="font-semibold text-slate-50">
            Punk Records
          </div>
          <p id={`${base}-brand-description`} className="text-slate-400">
            Unofficial deck builder, card index, and collection tracker for the
            One Piece Card Game.
          </p>
          <p
            id={`${base}-attribution-text`}
            className="text-xs leading-relaxed text-slate-400"
          >
            The literal and graphical information presented on this website about
            the One Piece Card Game, including card images and text, is copyright
            Eiichiro Oda/Shueisha, Toei Animation and/or Bandai. This website is
            not produced by, endorsed by, supported by, or affiliated with any of
            those copyright holders.
          </p>
          <p className="text-xs text-slate-400">
            Inspired by{" "}
            <a
              id={`${base}-attribution-scryfall-link`}
              href={SCRYFALL_HOME_URL}
              target="_blank"
              rel="noreferrer"
              className="text-blue-400 hover:underline"
            >
              Scryfall
            </a>
            .{" "}
            <a
              id={`${base}-attribution-scryfall-donate-link`}
              href={SCRYFALL_DONATE_URL}
              target="_blank"
              rel="noreferrer"
              className="text-blue-400 hover:underline"
            >
              Donate to Scryfall
            </a>
            .
          </p>
        </div>

        <div id={`${base}-cards-column`} className="space-y-2">
          <div className="font-medium text-slate-200">Cards</div>
          <ul className="space-y-1">
            <li>
              <Link
                id={`${base}-link-search`}
                href="/search"
                className="hover:text-blue-400"
              >
                Search
              </Link>
            </li>
            <li>
              <Link
                id={`${base}-link-advanced`}
                href="/advanced"
                className="hover:text-blue-400"
              >
                Advanced
              </Link>
            </li>
            <li>
              <Link
                id={`${base}-link-syntax`}
                href="/syntax"
                className="hover:text-blue-400"
              >
                Syntax
              </Link>
            </li>
            <li>
              <Link
                id={`${base}-link-sets`}
                href="/sets"
                className="hover:text-blue-400"
              >
                Sets
              </Link>
            </li>
          </ul>
        </div>

        <div id={`${base}-tools-column`} className="space-y-2">
          <div className="font-medium text-slate-200">Tools</div>
          <ul className="space-y-1">
            <li>
              <Link
                id={`${base}-link-deckbuild`}
                href="/deckbuild"
                className="hover:text-blue-400"
              >
                Deckbuild
              </Link>
            </li>
            <li>
              <Link
                id={`${base}-link-meta`}
                href="/meta"
                className="hover:text-blue-400"
              >
                Meta
              </Link>
            </li>
            <li>
              <Link
                id={`${base}-link-collection`}
                href="/collection"
                className="hover:text-blue-400"
              >
                Collection
              </Link>
            </li>
          </ul>
        </div>

        <div id={`${base}-more-column`} className="space-y-2">
          <div className="font-medium text-slate-200">More</div>
          <ul className="space-y-1">
            <li>
              <Link
                id={`${base}-link-cookies`}
                href="/cookies"
                className="hover:text-blue-400"
              >
                Cookies &amp; storage
              </Link>
            </li>
            <li>
              <a
                id={`${base}-link-donate-punkrecords`}
                href="https://ko-fi.com/hugoprudente"
                target="_blank"
                rel="noreferrer"
                className="hover:text-blue-400"
              >
                Donate to PunkRecords
              </a>
            </li>
            <li>
              <a
                id={`${base}-link-github-contact`}
                href="https://github.com/hugoprudente/punkrecords-next"
                target="_blank"
                rel="noreferrer"
                className="hover:text-blue-400"
              >
                GitHub (Contact Us)
              </a>
            </li>
          </ul>
        </div>
      </div>
      {buildVersion ? (
        <div
          id={`${base}-build-version`}
          className="border-t border-slate-800/80 bg-slate-950 px-4 py-2 text-center text-xs font-mono text-slate-400"
          title="Build date (UTC) and git short revision"
        >
          Build {buildVersion}
        </div>
      ) : null}
    </footer>
  );
}
